import seedrandom from 'seedrandom';

import { types as sdkTypes } from './sdkLoader';
import { LISTING_STATE_PUBLISHED } from './types';
import { convertUnitToSubUnit, unitDivisor } from './currency';
import { appendReferrerDiscovery } from './promotixDiscoveryLinks';

const { UUID, LatLng, Money } = sdkTypes;

export const PROMOTIX_DEFAULT_IMAGE_URL = '/static/images/PromoTix-DefaultImage.jpg';

const stableUuidString = seedKey => {
  const rng = seedrandom(`promotix-primary:${seedKey}`);
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
    .replace(/x/g, () => Math.floor(rng() * 16).toString(16))
    .replace(/y/g, () => (8 + Math.floor(rng() * 4)).toString(16));
};

const primaryEventStableKey = event =>
  String(event.event_id ?? event.id ?? event.event_title ?? event.social_share_url ?? '');

const primaryEventExternalHref = event => {
  const fromShare = appendReferrerDiscovery(event.social_share_url);
  if (fromShare) {
    return fromShare;
  }
  const id = event.event_id ?? event.id;
  if (id != null && id !== '') {
    return `https://promotix.com/?referrer=discovery&event_id=${encodeURIComponent(String(id))}`;
  }
  return 'https://promotix.com/?referrer=discovery';
};

/**
 * PromoTix primary `min_price` / `max_price` (major units) → Money for display / map.
 */
export const primaryAmountToMoney = (raw, currency) => {
  const div = unitDivisor(currency);
  if (raw == null || raw === '') {
    return new Money(0, currency);
  }
  const n = Number.parseFloat(String(raw).replace(/,/g, ''));
  if (!Number.isFinite(n) || n < 0) {
    return new Money(0, currency);
  }
  const sub = convertUnitToSubUnit(String(n), div);
  return new Money(sub, currency);
};

const priceMoneyForMap = (event, currency) => {
  const raw = event.min_price != null ? event.min_price : event.max_price;
  return primaryAmountToMoney(raw, currency);
};

const buildPrimaryEventImage = (event, uuidSeed) => {
  const url = event.image || PROMOTIX_DEFAULT_IMAGE_URL;
  return {
    id: new UUID(stableUuidString(`img-${uuidSeed}`)),
    type: 'image',
    attributes: {
      variants: {
        'listing-card': { url, width: 400, height: 400 },
        'listing-card-2x': { url, width: 800, height: 800 },
        'scaled-small': { url, width: 240, height: 240 },
        'scaled-medium': { url, width: 750, height: 750 },
      },
    },
  };
};

/**
 * Minimal Sharetribe-shaped listing for SearchMap (pins + info card).
 * Only built when the proxy attached `latitude` / `longitude`.
 */
export const primaryEventToMapListing = (event, currency) => {
  const lat = Number(event.latitude);
  const lng = Number(event.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  const href = primaryEventExternalHref(event);
  const key = primaryEventStableKey(event);
  return {
    id: new UUID(stableUuidString(key)),
    type: 'listing',
    attributes: {
      title: event.event_title || 'Event',
      description: '',
      geolocation: new LatLng(lat, lng),
      deleted: false,
      state: LISTING_STATE_PUBLISHED,
      price: priceMoneyForMap(event, currency),
      publicData: {
        promotixPrimaryEvent: true,
        socialShareUrl: event.social_share_url,
        externalHref: href,
      },
    },
    images: [buildPrimaryEventImage(event, key)],
  };
};

export const primaryEventsToMapListings = (events, currency) => {
  if (!Array.isArray(events) || !events.length) {
    return [];
  }
  return events.map(ev => primaryEventToMapListing(ev, currency)).filter(Boolean);
};
